import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Moon, Sun, Languages, LogOut, LayoutDashboard, UserRound, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useTheme } from "@/components/ThemeProvider";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { NotificationBell } from "@/components/NotificationBell";
import { ProfileDialog } from "@/components/ProfileDialog";
import { ChatWidget } from "@/components/ChatWidget";

export const Navbar = ({ assoc }) => {
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);

  const isAdmin = user && ["super_admin", "association_admin", "mosque_admin", "moderator"].includes(user.role);

  return (
    <header className="glass-header sticky top-0 z-50">
      {assoc && <ChatWidget slug={assoc.slug} />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/" data-testid="nav-logo" className="font-heading font-extrabold text-lg tracking-tight shrink-0">
            SHIALOC<span className="text-accent">.</span>
          </Link>
          {assoc && (
            <>
              <span className="text-border">/</span>
              <Link to={`/${assoc.slug}`} data-testid="nav-assoc-link" className="font-heading font-semibold truncate hover:text-accent transition-colors">
                {assoc.name}
              </Link>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" data-testid="lang-toggle" onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="gap-1.5 px-2.5">
            <Languages className="h-4 w-4" />
            <span className="text-xs font-mono-data uppercase">{lang}</span>
          </Button>
          <Button variant="ghost" size="icon" data-testid="theme-toggle" onClick={toggleTheme} className="h-9 w-9">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          {user && <NotificationBell />}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="ml-1 rounded-full outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring">
                  <Avatar className="h-8 w-8 border border-border">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="text-xs font-semibold">{user.name?.slice(0, 2).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                {isAdmin && (
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                    <LayoutDashboard className="h-4 w-4 mr-2" /> {t("dashboard")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-profile" onClick={() => setProfileOpen(true)}>
                  <UserCog className="h-4 w-4 mr-2" /> {t("my_profile")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button size="sm" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="ml-1 rounded-full px-4">
              <UserRound className="h-4 w-4 mr-1.5" /> {t("login")}
            </Button>
          )}
        </div>
      </div>
      {user && <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />}
    </header>
  );
};
